import {
  Box,
  useTheme,
  TextField,
  MenuItem,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { useStateContext } from "contexts/ContextProvider";
import React, { useState, useEffect, useCallback } from "react";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { useAuth } from "contexts/AuthContext";
import api from "services/api";
import { OVERLAY_NAMES } from "constants/Overlays";
import { usePageTitle } from "hooks/usePageTitle";

const BugReportNew = () => {
  usePageTitle("New Bug Report");
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { currentUser } = useAuth();
  const { overlayData, setOverlayName, triggerPageRefresh } = useStateContext();

  const isEditMode =
    overlayData?.Action === OVERLAY_NAMES.BUG_REPORT_ACTION_EDIT;

  const [isSaving, setIsSaving] = useState(false);
  const [title, setTitle] = useState(
    isEditMode ? overlayData.BugReport.Title : "",
  );
  const [description, setDescription] = useState(
    isEditMode ? overlayData.BugReport.Description : "",
  );
  const [category, setCategory] = useState(
    isEditMode ? overlayData.BugReport.Category : "Other",
  );
  const [priority, setPriority] = useState(
    isEditMode ? overlayData.BugReport.Priority : "Medium",
  );
  const [userAction, setUserAction] = useState(
    isEditMode ? overlayData.BugReport.UserAction || "" : "",
  );
  const [expectedBehavior, setExpectedBehavior] = useState(
    isEditMode ? overlayData.BugReport.ExpectedBehavior || "" : "",
  );
  const [actualBehavior, setActualBehavior] = useState(
    isEditMode ? overlayData.BugReport.ActualBehavior || "" : "",
  );
  const [stepsToReproduce, setStepsToReproduce] = useState(
    isEditMode ? overlayData.BugReport.StepsToReproduce || "" : "",
  );

  // Validation errors
  const [titleError, setTitleError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);

  // Auto-captured data
  const [pageURL, setPageURL] = useState("");
  const [pageTitle, setPageTitle] = useState("");
  const [browserInfo, setBrowserInfo] = useState("");
  const [osInfo, setOSInfo] = useState("");
  const [screenResolution, setScreenResolution] = useState("");
  const [userAgent, setUserAgent] = useState("");

  useEffect(() => {
    // Only capture automatic data for new bug reports
    if (!isEditMode) {
      setPageURL(window.location.href);
      setPageTitle(document.title);
      setUserAgent(navigator.userAgent);
      setScreenResolution(`${window.screen.width}x${window.screen.height}`);

      // Detect browser
      const ua = navigator.userAgent;
      let browser = "Unknown";
      if (ua.includes("Firefox")) browser = "Firefox";
      else if (ua.includes("Chrome")) browser = "Chrome";
      else if (ua.includes("Safari")) browser = "Safari";
      else if (ua.includes("Edge")) browser = "Edge";
      else if (ua.includes("Opera") || ua.includes("OPR")) browser = "Opera";
      setBrowserInfo(browser);

      // Detect OS
      let os = "Unknown";
      if (ua.includes("Win")) os = "Windows";
      else if (ua.includes("Mac")) os = "MacOS";
      else if (ua.includes("Linux")) os = "Linux";
      else if (ua.includes("Android")) os = "Android";
      else if (ua.includes("iOS")) os = "iOS";
      setOSInfo(os);
    } else {
      // Load existing data for edit mode
      setPageURL(overlayData.BugReport.PageURL || "");
      setPageTitle(overlayData.BugReport.PageTitle || "");
      setBrowserInfo(overlayData.BugReport.BrowserInfo || "");
      setOSInfo(overlayData.BugReport.OSInfo || "");
      setScreenResolution(overlayData.BugReport.ScreenResolution || "");
      setUserAgent(overlayData.BugReport.UserAgent || "");
    }

    // Capture console errors if any
    const errors = [];
    const originalConsoleError = console.error;
    console.error = function (...args) {
      errors.push(args.join(" "));
      originalConsoleError.apply(console, args);
    };

    return () => {
      console.error = originalConsoleError;
    };
  }, [
    isEditMode,
    overlayData?.BugReport?.PageURL,
    overlayData?.BugReport?.PageTitle,
    overlayData?.BugReport?.BrowserInfo,
    overlayData?.BugReport?.OSInfo,
    overlayData?.BugReport?.ScreenResolution,
    overlayData?.BugReport?.UserAgent,
  ]);

  const categories = [
    "UI/UX",
    "Functionality",
    "Performance",
    "Data",
    "Security",
    "Other",
  ];

  const priorities = ["Low", "Medium", "High", "Critical"];

  const handleSave = useCallback(() => {
    // Validate required fields
    if (!title.trim()) {
      setTitleError(true);
      return;
    }
    if (!description.trim()) {
      setDescriptionError(true);
      return;
    }

    setIsSaving(true);

    const data = {
      Title: title,
      Description: description,
      Category: category,
      Priority: priority,
      UserAction: userAction || null,
      ExpectedBehavior: expectedBehavior || null,
      ActualBehavior: actualBehavior || null,
      StepsToReproduce: stepsToReproduce || null,
      PageURL: pageURL,
      PageTitle: pageTitle,
      BrowserInfo: browserInfo,
      OSInfo: osInfo,
      ScreenResolution: screenResolution,
      UserAgent: userAgent,
      ReportedBy: currentUser.id,
      Status: isEditMode ? overlayData.BugReport.Status : "New",
    };

    const endpoint = isEditMode
      ? "/bugReports/updateBugReport"
      : "/bugReports/addNewBugReport";
    const postData = isEditMode
      ? { data: { ...data, id: overlayData.BugReportID } }
      : { data };

    api
      .post(endpoint, postData)
      .then((response) => {
        setIsSaving(false);
        triggerPageRefresh(
          isEditMode
            ? "Bug Report Updated Successfully!"
            : "Bug Report Submitted Successfully!",
          theme,
        );
        setOverlayName("");
      })
      .catch((err) => {
        console.error("Error submitting bug report:", err);
        setIsSaving(false);
        alert("Failed to submit bug report. Please try again.");
      });
  }, [
    title,
    description,
    category,
    priority,
    userAction,
    expectedBehavior,
    actualBehavior,
    stepsToReproduce,
    pageURL,
    pageTitle,
    browserInfo,
    osInfo,
    screenResolution,
    userAgent,
    currentUser.id,
    isEditMode,
    overlayData,
    setOverlayName,
    triggerPageRefresh,
    theme,
  ]);

  const attemptSave = useCallback(() => {
    if (isSaving) {
      return;
    }

    handleSave();
  }, [handleSave, isSaving]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const target = e.target;
      const tagName = target?.tagName;
      const isTextArea = tagName === "TEXTAREA";
      const isContentEditable = !!target?.isContentEditable;
      const isInsideDialog =
        target instanceof Element && !!target.closest('[role="dialog"]');

      if (
        e.key === "Enter" &&
        !e.shiftKey &&
        !isTextArea &&
        !isContentEditable &&
        !isInsideDialog
      ) {
        e.preventDefault();
        attemptSave();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [attemptSave]);

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: theme.palette.background.alt,
          width: { xs: "100%", sm: "90%", md: "75%", lg: "60%" },
          minWidth: { xs: "100%", sm: "600px" },
          maxWidth: "1200px",
          maxHeight: "80%",
          display: "flex",
          borderRadius: "1.5rem",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          color: theme.palette.text.primary,
          p: "0 .2rem 1.5rem 0",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            width: "100%",
            flexDirection: "row",
            p: ".75rem 2rem",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              flexDirection: "row",
            }}
          >
            <IconButton
              color="primary"
              size="large"
              onClick={() => setOverlayName("")}
            >
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>

            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
              {isEditMode ? "Edit Bug Report" : "Report a Bug"}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "flex-end",
              width: "40%",
              flexDirection: "row",
            }}
          >
            {isMobile ? (
              <Tooltip title="Cancel" placement="top">
                <IconButton
                  color="primary"
                  onClick={() => setOverlayName("")}
                  sx={{
                    width: "40px",
                    height: "40px",
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: "12px",
                  }}
                >
                  <DisabledByDefaultRoundedIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                color="primary"
                variant="outlined"
                size="large"
                endIcon={<DisabledByDefaultRoundedIcon />}
                onClick={() => setOverlayName("")}
              >
                Cancel
              </Button>
            )}

            <Box p="0 .5rem" />

            {isMobile ? (
              <Tooltip
                title={
                  isSaving
                    ? isEditMode
                      ? "Updating..."
                      : "Submitting..."
                    : isEditMode
                      ? "Update"
                      : "Submit"
                }
                placement="top"
              >
                <span>
                  <IconButton
                    disabled={isSaving}
                    color="primary"
                    onClick={attemptSave}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.background.default,
                      borderRadius: "12px",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                      },
                      "&.Mui-disabled": {
                        backgroundColor:
                          theme.palette.action.disabledBackground,
                        color: theme.palette.action.disabled,
                      },
                    }}
                  >
                    <SaveRoundedIcon />
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <Button
                disabled={isSaving}
                color="primary"
                variant="contained"
                size="large"
                endIcon={<SaveRoundedIcon />}
                onClick={attemptSave}
              >
                {isSaving
                  ? isEditMode
                    ? "Updating..."
                    : "Submitting..."
                  : isEditMode
                    ? "Update"
                    : "Submit"}
              </Button>
            )}
          </Box>
        </Box>

        {/* Form Content */}
        <Box
          sx={{
            p: "1rem 2.3rem 1rem 2.5rem",
            flex: "1 1 auto",
            overflowY: "auto",
            width: "100%",
            minHeight: 0,
            borderRadius: "1.5rem",
            backgroundColor: theme.palette.background.default,
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
            },
          }}
        >
          <Grid2 container spacing={2}>
            {/* Basic Information */}
            <Grid2 size={{ xs: 12 }}>
              <Typography variant="h4" fontWeight="bold">
                Bug Information
              </Typography>
            </Grid2>

            <Grid2 size={{ xs: 12, md: 8 }}>
              <TextField
                size="small"
                id="Title"
                label="Bug Title *"
                error={titleError}
                value={title}
                helperText={titleError ? "Title is required" : ""}
                onChange={(event) => {
                  setTitle(event.target.value);
                  setTitleError(event.target.value.trim() === "");
                }}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 2 }}>
              <TextField
                size="small"
                select
                id="Priority"
                label="Priority"
                value={priority}
                onChange={(event) => setPriority(event.target.value)}
              >
                {priorities.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid2>

            <Grid2 size={{ xs: 12, md: 2 }}>
              <TextField
                size="small"
                select
                id="Category"
                label="Category"
                value={category}
                onChange={(event) => setCategory(event.target.value)}
              >
                {categories.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid2>

            <Grid2 size={{ xs: 12 }}>
              <TextField
                size="small"
                multiline
                rows={3}
                id="Description"
                label="Description *"
                error={descriptionError}
                value={description}
                helperText={
                  descriptionError
                    ? "Description is required"
                    : "Describe the issue you're experiencing"
                }
                onChange={(event) => {
                  setDescription(event.target.value);
                  setDescriptionError(event.target.value.trim() === "");
                }}
              />
            </Grid2>

            {/* Detailed Information */}
            <Grid2 size={{ xs: 12 }}>
              <Typography variant="h5" fontWeight="bold" sx={{ mt: 1 }}>
                Additional Details (Optional)
              </Typography>
            </Grid2>

            <Grid2 size={{ xs: 12 }}>
              <TextField
                size="small"
                multiline
                rows={2}
                id="UserAction"
                label="What were you trying to do?"
                value={userAction}
                helperText="Describe the action you were performing when the bug occurred"
                onChange={(event) => setUserAction(event.target.value)}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                size="small"
                multiline
                rows={2}
                id="ExpectedBehavior"
                label="What did you expect to happen?"
                value={expectedBehavior}
                onChange={(event) => setExpectedBehavior(event.target.value)}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                size="small"
                multiline
                rows={2}
                id="ActualBehavior"
                label="What actually happened?"
                value={actualBehavior}
                onChange={(event) => setActualBehavior(event.target.value)}
              />
            </Grid2>

            <Grid2 size={{ xs: 12 }}>
              <TextField
                size="small"
                multiline
                rows={2}
                id="StepsToReproduce"
                label="Steps to Reproduce"
                value={stepsToReproduce}
                helperText="List the steps to reproduce this issue (e.g., 1. Go to... 2. Click on... 3. See error)"
                onChange={(event) => setStepsToReproduce(event.target.value)}
              />
            </Grid2>

            {/* Auto-captured Information */}
            <Grid2 size={{ xs: 12 }}>
              <Typography variant="h5" fontWeight="bold" sx={{ mt: 1 }}>
                System Information (Auto-captured)
              </Typography>
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                size="small"
                disabled
                id="PageURL"
                label="Page URL"
                value={pageURL}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                size="small"
                disabled
                id="PageTitle"
                label="Page Title"
                value={pageTitle}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 4 }}>
              <TextField
                size="small"
                disabled
                id="BrowserInfo"
                label="Browser"
                value={browserInfo}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 4 }}>
              <TextField
                size="small"
                disabled
                id="OSInfo"
                label="Operating System"
                value={osInfo}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 4 }}>
              <TextField
                size="small"
                disabled
                id="ScreenResolution"
                label="Screen Resolution"
                value={screenResolution}
              />
            </Grid2>
          </Grid2>
        </Box>
      </Box>
    </Box>
  );
};

export default BugReportNew;
